import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, User, PenLine } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const SERVICES = [
  "Domestic Cleaning",
  "Office Cleaning",
  "End of Tenancy Cleaning",
  "Carpet & Upholstery Cleaning",
  "Residential Cleaning",
  "Laundry & Ironing",
  "Vacuuming",
  "Other",
];

const Stars = ({ rating }) => (
  <span className="flex gap-0.5 text-teal-500">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < rating ? "fill-current" : "text-slate-200"}`} />
    ))}
  </span>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", service: SERVICES[0], rating: 5, text: "" });

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you! Your review has been submitted for moderation.");
      setOpen(false);
      setForm({ name: "", service: SERVICES[0], rating: 5, text: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="bg-slate-50 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 flex flex-col gap-8 lg:mb-20 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="max-w-2xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-teal-700">
              Reviews & Testimonials
            </p>
            <h2 className="font-serif text-4xl font-medium tracking-tight text-slate-900 sm:text-5xl">
              Loved by homes &amp; offices <span className="italic text-teal-700">alike.</span>
            </h2>
          </div>
          <button
            data-testid="leave-review-button"
            onClick={() => setOpen(true)}
            className="flex w-fit items-center gap-2 rounded-full bg-teal-700 px-7 py-3.5 text-sm font-bold text-white shadow-sm transition-transform duration-300 hover:scale-105 hover:bg-teal-600"
          >
            <PenLine className="h-4 w-4" /> Leave a Review
          </button>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {reviews.map((r, i) => (
            <motion.figure
              key={r.id}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col justify-between rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition-transform duration-500 hover:-translate-y-1 hover:shadow-lg"
            >
              <div>
                <Stars rating={r.rating} />
                <blockquote className="mt-4 font-serif text-lg italic leading-relaxed text-slate-800">
                  “{r.text}”
                </blockquote>
              </div>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                  <User className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-slate-900">{r.name}</span>
                  <span className="block text-xs font-semibold text-slate-400">{r.service}</span>
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="review-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">Share your experience</DialogTitle>
            <DialogDescription>
              Your review will appear on the site once our team has approved it.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="review-name" className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                Your name
              </label>
              <input
                id="review-name"
                data-testid="review-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="e.g. Sarah M."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="review-service" className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                Service used
              </label>
              <select
                id="review-service"
                data-testid="review-service-select"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-teal-500"
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Rating</span>
              <div className="flex gap-1" data-testid="review-rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`review-star-${n}`}
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-1"
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  >
                    <Star
                      className={`h-6 w-6 transition-colors ${
                        n <= form.rating ? "fill-teal-500 text-teal-500" : "text-slate-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="review-text" className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                Your review
              </label>
              <textarea
                id="review-text"
                data-testid="review-text-input"
                required
                minLength={10}
                rows={4}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Tell us how we did…"
              />
            </div>
            <button
              data-testid="review-submit-button"
              type="submit"
              disabled={submitting}
              className="mt-2 rounded-full bg-teal-700 py-3.5 text-sm font-bold text-white transition-colors hover:bg-teal-600 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit for Moderation"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
